import { useEffect, useMemo, useState } from 'react';
import { Download, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../../components/auth-context';
import { Input } from '../../../../components/ui/input';
import { Button } from '../../../../components/ui/button';
import { PapersTable } from '../../../components/committee/papers-table';
import { AssignEvaluatorsDialog } from '../../../components/committee/assign-evaluators-dialog';
import { EvaluationLinksDialog } from '../../../components/committee/evaluation-links-dialog';
import { PaperDocumentDialog } from '../../../components/committee/paper-document-dialog';
import { ReassignEvaluatorsDialog } from '../../../components/committee/reassign-evaluators-dialog';
import { MaterialLinkDialog } from '../../../components/committee/material-link-dialog';
import api from '@/api';
import {
  type CommitteePaperFilter,
  type CommitteePaperItem,
  type CommitteePaperNationalityFilter,
  type CommitteePaperProcessFilter,
  useCommitteePapers,
  useExportFinalSpeakers,
  useExportOperationalSpeakers,
  useStartSecondRound,
} from '@/hooks/use-committee-papers';
import { PERMISSIONS } from '@/hooks/usePermission';

interface CommitteePapersPageProps {
  eventId: string;
}

const FILTERS: Array<{ key: CommitteePaperFilter; label: string }> = [
  { key: 'ALL', label: 'Todas' },
  { key: 'PENDING', label: 'Pendientes' },
  { key: 'UNDER_EVAL', label: 'En evaluacion' },
  { key: 'APPROVED', label: 'Aprobadas' },
  { key: 'REJECTED', label: 'Rechazadas' },
];

function sanitizeDownloadFilenameSegment(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, ' ')
    .trim()
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^[._]+|[._]+$/g, '');
}

function buildFinalMaterialDownloadFilename(paper: CommitteePaperItem): string {
  const folio = sanitizeDownloadFilenameSegment(paper.folio);
  const author = sanitizeDownloadFilenameSegment(paper.author.name)
    .slice(0, 120)
    .replace(/[._]+$/g, '');

  if (!folio || !author) {
    throw new Error('No fue posible construir un nombre de descarga valido.');
  }

  return `${folio}_${author}.zip`;
}

export function CommitteePapersPage({ eventId }: CommitteePapersPageProps) {
  const [filter, setFilter] = useState<CommitteePaperFilter>('ALL');
  const [processStatus, setProcessStatus] = useState<CommitteePaperProcessFilter>('ALL');
  const [nationality, setNationality] = useState<CommitteePaperNationalityFilter>('ALL');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [assignPaper, setAssignPaper] = useState<CommitteePaperItem | null>(null);
  const [reassignPaper, setReassignPaper] = useState<CommitteePaperItem | null>(null);
  const [documentPaper, setDocumentPaper] = useState<CommitteePaperItem | null>(null);
  const [linksPaper, setLinksPaper] = useState<CommitteePaperItem | null>(null);
  const [materialLinkPaper, setMaterialLinkPaper] = useState<CommitteePaperItem | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const normalizedSearch = search.trim();
      setDebouncedSearch(normalizedSearch.length >= 3 ? normalizedSearch.slice(0, 100) : '');
    }, 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const { user } = useAuth();
  const { data: papers = [], isLoading, isFetching, refetch } = useCommitteePapers(
    eventId,
    filter,
    processStatus,
    nationality,
    debouncedSearch,
  );
  const startSecondRoundMutation = useStartSecondRound(eventId);
  const exportFinalSpeakersMutation = useExportFinalSpeakers();
  const exportOperationalSpeakersMutation = useExportOperationalSpeakers();

  const eventPermissions = user?.permissionsByEvent?.[eventId] || [];
  const canExportFinalSpeakers = Boolean(
    user?.isSuperAdmin
    || (
      eventPermissions.includes(PERMISSIONS.REVIEW_PAPERS)
      && (
        eventPermissions.includes(PERMISSIONS.EXPORT_DATA)
        || eventPermissions.includes(PERMISSIONS.MANAGE_COMMITTEE)
      )
    ),
  );

  const handleStartSecondRound = async (paper: CommitteePaperItem) => {
    try {
      await startSecondRoundMutation.mutateAsync({ paperId: paper.id });
      toast.success('Segunda ronda iniciada correctamente.');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'No se pudo iniciar la segunda ronda.');
    }
  };

  const orderedPapers = useMemo(() => papers, [papers]);

  const handleExportFinalSpeakers = async () => {
    try {
      await exportFinalSpeakersMutation.mutateAsync(eventId);
      toast.success('Padron final de ponentes descargado correctamente.');
    } catch {
      toast.error('No se pudo descargar el padron final de ponentes.');
    }
  };

  const handleExportOperationalSpeakers = async () => {
    try {
      await exportOperationalSpeakersMutation.mutateAsync({
        eventId,
        tab: filter,
        processStatus,
        nationality,
        search: debouncedSearch,
      });
      toast.success('Vista operativa exportada correctamente.');
    } catch {
      toast.error('No se pudo exportar la vista operativa de ponentes.');
    }
  };

  const handleDownloadZip = async (paper: CommitteePaperItem) => {
    try {
      const filename = buildFinalMaterialDownloadFilename(paper);
      const response = await api.get(`/speaker/material-download/${encodeURIComponent(paper.id)}`, {
        responseType: 'blob',
      });

      const blobUrl = window.URL.createObjectURL(response.data as Blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('No se pudo descargar el ZIP.', {
        description: 'El material final no esta disponible.',
      });
    }
  };

  const clearOperationalFilters = () => {
    setProcessStatus('ALL');
    setNationality('ALL');
    setSearch('');
  };

  if (!eventId) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-500">
        Seleccione un evento para gestionar ponencias del comite.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h2 className="text-5xl font-bold text-[#1e3a5f]">Ponencias del Comite</h2>
          <p className="mt-1 text-lg text-gray-600">Gestion de ponencias registradas y asignacion de evaluadores</p>
        </div>
        {canExportFinalSpeakers && (
          <Button
            type="button"
            variant="outline"
            onClick={handleExportFinalSpeakers}
            disabled={exportFinalSpeakersMutation.isPending}
            className="shrink-0"
          >
            <Download className="mr-2 h-4 w-4" />
            {exportFinalSpeakersMutation.isPending ? 'Generando...' : 'Descargar padrón oficial (QR)'}
          </Button>
        )}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-4 grid gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 md:grid-cols-2 xl:grid-cols-4">
          <label className="space-y-1 text-sm text-gray-700">
            <span>Proceso logístico</span>
            <select
              value={processStatus}
              onChange={(event) => setProcessStatus(event.target.value as CommitteePaperProcessFilter)}
              className="h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-sm outline-none focus:border-[#1e3a5f] focus:ring-2 focus:ring-[#1e3a5f]/20"
            >
              <option value="ALL">Todos</option>
              <option value="APPROVED_TO_PAY">Pendiente de pago</option>
              <option value="PAID">Pagado, pendiente de material final</option>
              <option value="MATERIAL_UPLOADED">Material final recibido, pendiente de QR</option>
              <option value="CREDENTIAL_ISSUED">QR emitido</option>
            </select>
          </label>

          <label className="space-y-1 text-sm text-gray-700">
            <span>Nacionalidad</span>
            <select
              value={nationality}
              onChange={(event) => setNationality(event.target.value as CommitteePaperNationalityFilter)}
              className="h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-sm outline-none focus:border-[#1e3a5f] focus:ring-2 focus:ring-[#1e3a5f]/20"
            >
              <option value="ALL">Todas</option>
              <option value="MEXICAN">Mexicana</option>
              <option value="FOREIGN">Extranjera</option>
            </select>
          </label>

          <label className="space-y-1 text-sm text-gray-700">
            <span>Buscar por folio, título o autor</span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                maxLength={100}
                className="pl-9"
                placeholder="Mínimo 3 caracteres..."
              />
            </div>
          </label>

          <div className="flex flex-wrap items-end justify-end gap-2">
            {canExportFinalSpeakers && (
              <Button
                type="button"
                variant="outline"
                onClick={handleExportOperationalSpeakers}
                disabled={exportOperationalSpeakersMutation.isPending}
              >
                <Download className="mr-2 h-4 w-4" />
                {exportOperationalSpeakersMutation.isPending ? 'Exportando...' : 'Exportar resultados filtrados'}
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={clearOperationalFilters}
              disabled={processStatus === 'ALL' && nationality === 'ALL' && !search}
            >
              Limpiar filtros
            </Button>
            <Button type="button" onClick={() => void refetch()} disabled={isFetching}>
              <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>
        </div>

        <div className="mb-4 flex justify-end">
          <div className="flex flex-wrap gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
            {FILTERS.map((item) => (
              <Button
                key={item.key}
                size="sm"
                onClick={() => setFilter(item.key)}
                className={
                  filter === item.key
                    ? 'bg-[#1e3a5f] text-white hover:bg-[#1e3a5f]'
                    : 'bg-transparent text-gray-700 hover:bg-white'
                }
              >
                {item.label}
              </Button>
            ))}
          </div>
        </div>

        <PapersTable
          papers={orderedPapers}
          isLoading={isLoading || isFetching}
          onAssign={(paper) => setAssignPaper(paper)}
          onReassign={(paper) => setReassignPaper(paper)}
          onStartSecondRound={handleStartSecondRound}
          onManageDocument={(paper) => setDocumentPaper(paper)}
          onManageLinks={(paper) => setLinksPaper(paper)}
          onManageMaterialLink={(paper) => setMaterialLinkPaper(paper)}
          onDownloadZip={handleDownloadZip}
          showMaterialLinkAction={filter === 'APPROVED'}
          showFinalZipDownloadAction={filter === 'APPROVED'}
        />
      </div>

      <AssignEvaluatorsDialog
        open={Boolean(assignPaper)}
        onOpenChange={(open) => {
          if (!open) setAssignPaper(null);
        }}
        eventId={eventId}
        paper={assignPaper}
      />

      <ReassignEvaluatorsDialog
        open={Boolean(reassignPaper)}
        onOpenChange={(open) => {
          if (!open) setReassignPaper(null);
        }}
        eventId={eventId}
        paper={reassignPaper}
      />

      <PaperDocumentDialog
        open={Boolean(documentPaper)}
        onOpenChange={(open) => {
          if (!open) setDocumentPaper(null);
        }}
        eventId={eventId}
        paper={documentPaper}
      />

      <EvaluationLinksDialog
        open={Boolean(linksPaper)}
        onOpenChange={(open) => {
          if (!open) setLinksPaper(null);
        }}
        eventId={eventId}
        paper={linksPaper}
      />

      <MaterialLinkDialog
        open={Boolean(materialLinkPaper)}
        onOpenChange={(open) => {
          if (!open) setMaterialLinkPaper(null);
        }}
        paper={materialLinkPaper}
      />
    </div>
  );
}
